/**
 * UtilsService - Complete utility service for loan calculations, payment flows, and state management
 *
 * This service provides:
 * - EMI calculations and fee calculations
 * - Payment amount determination (next due, total due)
 * - Date formatting and calculations
 * - Currency formatting
 * - Loan status determination
 * - Voucher code formatting
 * - NACH eligibility checking
 * - Device fingerprinting
 * - Razorpay/Paytm payment initiation
 */

import DOMPurify from 'dompurify';
import { RAZORPAYID } from '../../environments/environment';
import {
  Loan,
  EmiDue,
  Fee,
  createDefaultEmiDue,
  createDefaultFee
} from '../types/loan.types';
import { Repayment } from '../types/repayment.types';
import { NachRepayment, shouldShowQuickPay } from '../types/nach.types';
import {
  RazorpayOptions,
  RazorpayResponse,
  PaymentType,
  RazorpayExternalResponse
} from '../types/payment.types';

// Window.Razorpay is declared in payment.types.ts
// Declare ClientJS on window
declare global {
  interface Window {
    ClientJS?: new () => {
      getFingerprint: () => string;
      getOS: () => string;
      getBrowser: () => string;
      getDevice: () => string;
    };
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface CalculatedFees {
  fees: Fee;
  allFeesPaid: boolean;
}

export interface NextEmiDueResult {
  nextEmiDue: EmiDue;
  firstUnpaidIndex: number;
}

export interface OtherEmiDueResult {
  otherEmiDue: EmiDue;
  payAll: boolean;
}

export interface AmountToPayResult {
  amountToPay: number;
  paymentType: 'single' | 'multiple' | 'all';
}

export interface DeviceInfo {
  deviceId: string;
  os: string;
  browser: string;
  device: string;
}

// ============================================================================
// UTILS SERVICE CLASS
// ============================================================================

class UtilsService {
  private oneDay = 24 * 60 * 60 * 1000;
  
  // Active EMI statuses for calculations
  private activeEmiStatuses = ['Pending', 'PartiallyPaid', 'Late', 'To Be paid', 'Upcoming'];

  // ============================================================================
  // FEE CALCULATIONS
  // ============================================================================

  /**
   * Calculate fees for a loan from repayments
   */
  calculateFees(loan: Loan): CalculatedFees {
    const fees = createDefaultFee();
    let allFeesPaid = true;

    if (!loan.Repayments || loan.Repayments.length === 0) {
      return { fees, allFeesPaid };
    }

    loan.Repayments.forEach((repayment: Repayment) => {
      fees.LateFee += repayment.LateFee || 0;
      fees.ProcessingFee += repayment.ProcessingFee || 0;
      fees.InsuranceFee += repayment.InsuranceFee || 0;
      fees.TotalFees += (repayment.FeesDue || 0) + (repayment.PenaltyDue || 0);
      fees.FeesPaid += (repayment.FeesPaid || 0) + (repayment.PenaltyPaid || 0);
    });

    // Check if all fees are paid
    if (fees.TotalFees > fees.FeesPaid) {
      allFeesPaid = false;
    }

    return { fees, allFeesPaid };
  }

  // ============================================================================
  // NEXT EMI DUE CALCULATIONS
  // ============================================================================

  /**
   * Calculate the next EMI due details
   */
  calculateNextEmiDue(loan: Loan): NextEmiDueResult {
    const nextEmiDue = createDefaultEmiDue();
    let firstUnpaidIndex = -1;

    if (!loan.Repayments || loan.Repayments.length === 0) {
      return { nextEmiDue, firstUnpaidIndex };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the first unpaid EMI
    for (let i = 0; i < loan.Repayments.length; i++) {
      const repayment = loan.Repayments[i];
      const state = repayment.FinalRepaymentState || repayment.State;
      
      if (this.activeEmiStatuses.includes(state || '')) {
        const emiDate = new Date(repayment.DueDate);
        emiDate.setHours(0, 0, 0, 0);

        // Calculate status based on date
        const isPastDue = emiDate <= today;
        const isWithin30Days = this.isWithinDays(emiDate, today, 30);

        if (isPastDue || isWithin30Days) {
          firstUnpaidIndex = i;
          // Uses TotalDue directly (not TotalDueAmount - TotalPaidAmount)
          nextEmiDue.amount = repayment.TotalDue || repayment.TotalDueAmount || 0;
          nextEmiDue.dueAmount = repayment.TotalDue || repayment.TotalDueAmount || 0;
          nextEmiDue.date = repayment.DueDate;
          nextEmiDue.index = i;
          nextEmiDue.feesDue = (repayment.FeesDue || 0) + (repayment.PenaltyDue || 0) - 
                               (repayment.FeesPaid || 0) - (repayment.PenaltyPaid || 0);
          break;
        }
      }
    }

    // If no upcoming EMI found, get the next "To Be paid"
    if (firstUnpaidIndex === -1) {
      for (let i = 0; i < loan.Repayments.length; i++) {
        const repayment = loan.Repayments[i];
        const state = repayment.FinalRepaymentState || repayment.State;
        
        if (state === 'To Be paid' || state === 'Pending') {
          firstUnpaidIndex = i;
          nextEmiDue.amount = repayment.TotalDue || repayment.TotalDueAmount || 0;
          nextEmiDue.dueAmount = repayment.TotalDue || repayment.TotalDueAmount || 0;
          nextEmiDue.date = repayment.DueDate;
          nextEmiDue.index = i;
          nextEmiDue.feesDue = (repayment.FeesDue || 0) + (repayment.PenaltyDue || 0) - 
                               (repayment.FeesPaid || 0) - (repayment.PenaltyPaid || 0);
          break;
        }
      }
    }

    return { nextEmiDue, firstUnpaidIndex };
  }

  // ============================================================================
  // OTHER EMI DUE CALCULATIONS
  // ============================================================================

  /**
   * Calculate other EMI dues (for pay all scenarios)
   */
  calculateOtherEmiDue(loan: Loan, firstUnpaidIndex: number): OtherEmiDueResult {
    const otherEmiDue = createDefaultEmiDue();
    let payAll = false;

    if (!loan.Repayments || loan.Repayments.length === 0 || firstUnpaidIndex < 0) {
      return { otherEmiDue, payAll };
    }

    let totalOtherAmount = 0;
    let totalOtherFees = 0;
    let lastDate = '';

    // Sum all other pending EMIs after the first unpaid
    for (let i = firstUnpaidIndex + 1; i < loan.Repayments.length; i++) {
      const repayment = loan.Repayments[i];
      const state = repayment.FinalRepaymentState || repayment.State;

      if (this.activeEmiStatuses.includes(state || '')) {
        // Uses TotalDue (not TotalDueAmount - TotalPaidAmount)
        totalOtherAmount += repayment.TotalDue || repayment.TotalDueAmount || 0;
        totalOtherFees += (repayment.FeesDue || 0) + (repayment.PenaltyDue || 0) - 
                          (repayment.FeesPaid || 0) - (repayment.PenaltyPaid || 0);
        lastDate = repayment.DueDate;
      }
    }

    if (totalOtherAmount > 0) {
      otherEmiDue.amount = totalOtherAmount;
      otherEmiDue.date = lastDate;
      otherEmiDue.feesDue = totalOtherFees;
      payAll = true;
    }

    return { otherEmiDue, payAll };
  }

  // ============================================================================
  // TOTAL AMOUNT TO PAY
  // ============================================================================

  /**
   * Calculate total amount to pay based on loan state
   */
  calculateAmountToPay(
    loan: Loan,
    payAll: boolean = false
  ): AmountToPayResult {
    const { nextEmiDue, firstUnpaidIndex } = this.calculateNextEmiDue(loan);
    const { otherEmiDue } = this.calculateOtherEmiDue(loan, firstUnpaidIndex);

    let amountToPay = nextEmiDue.amount;
    let paymentType: 'single' | 'multiple' | 'all' = 'single';

    // Add moratorium interest if applicable
    if (loan.MoratoriumStatus === 'OPTIN_COMPLETED' && loan.MoratoriumInterestDue) {
      amountToPay += loan.MoratoriumInterestDue;
    }

    // Add other EMIs if pay all is selected
    if (payAll && otherEmiDue.amount > 0) {
      amountToPay += otherEmiDue.amount;
      paymentType = 'all';
    } else if (firstUnpaidIndex > 0) {
      paymentType = 'multiple';
    }

    return { amountToPay: Math.round(amountToPay * 100) / 100, paymentType };
  }

  // ============================================================================
  // QUICK PAY VISIBILITY
  // ============================================================================

  /**
   * Determine if Quick Pay should be shown for a loan
   */
  calculateShowQuickPay(
    loan: Loan,
    nachRepayment: NachRepayment | null,
    nachRepaymentArray: NachRepayment[],
    nachRepaymentError: boolean
  ): 0 | 1 | 2 | 3 {
    const { amountToPay } = this.calculateAmountToPay(loan);
    
    return shouldShowQuickPay(
      nachRepayment,
      nachRepaymentArray,
      nachRepaymentError,
      amountToPay,
      loan.Status
    );
  }

  // ============================================================================
  // DATE UTILITIES
  // ============================================================================

  /**
   * Calculate the number of days since a given date
   */
  calculateDaysSince(date: Date | string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const past = new Date(date);
    past.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - past.getTime();
    return Math.ceil(diffTime / this.oneDay);
  }

  /**
   * Calculate the number of days until a payment is due
   */
  calculateDaysUntilDue(dueDate: Date | string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / this.oneDay);
  }

  /**
   * Check if date1 is within specified days of date2
   */
  private isWithinDays(date1: Date, date2: Date, days: number): boolean {
    const diffTime = date1.getTime() - date2.getTime();
    const diffDays = Math.ceil(diffTime / this.oneDay);
    return diffDays >= 0 && diffDays <= days;
  }

  /**
   * Format a date to a readable string
   */
  formatDate(date: Date | string, format: string = 'DD MMM YYYY'): string {
    const d = new Date(date);
    
    const day = d.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    
    return format
      .replace('DD', day.toString().padStart(2, '0'))
      .replace('MMM', month)
      .replace('YYYY', year.toString());
  }

  // ============================================================================
  // CURRENCY UTILITIES
  // ============================================================================

  /**
   * Format currency in INR.
   * Rules: toFixed(2), Indian number format, hide decimal if '.00'
   */
  formatCurrency(amount: number): string {
    if (!amount && amount !== 0) return '₹0';
    let x = Number(amount).toFixed(2);
    const decimal = x.split('.');
    const beforeDecimal = decimal[0];
    const afterDecimal = decimal[1];
    x = beforeDecimal;
    let lastThree = x.substring(x.length - 3);
    const otherNumbers = x.substring(0, x.length - 3);
    if (otherNumbers !== '' && otherNumbers !== '-') {
      lastThree = `,${lastThree}`;
    }
    let result = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
    if (afterDecimal && afterDecimal !== '00') {
      result = `${result}.${afterDecimal}`;
    }
    return '₹' + result;
  }

  /**
   * Format a number with Indian numbering system (lakhs, crores)
   */
  formatIndianNumber(amount: number): string {
    return new Intl.NumberFormat('en-IN').format(amount);
  }

  // ============================================================================
  // LOAN STATUS UTILITIES
  // ============================================================================

  /**
   * Get loan status text based on due date
   */
  getLoanStatusText(loan: Loan): string {
    if (!['Active', 'InArrears'].includes(loan.Status)) {
      return loan.Status;
    }
    
    const { nextEmiDue } = this.calculateNextEmiDue(loan);
    
    if (!nextEmiDue.date) {
      return loan.Status;
    }

    const daysUntilDue = this.calculateDaysUntilDue(nextEmiDue.date);
    
    if (daysUntilDue < 0) {
      const daysOverdue = Math.abs(daysUntilDue);
      return daysOverdue === 1 ? 'Overdue for 1 day' : `Overdue for ${daysOverdue} days`;
    } else if (daysUntilDue === 0) {
      return 'Due today';
    } else if (daysUntilDue === 1) {
      return 'Due in 1 day';
    } else {
      return `Due in ${daysUntilDue} days`;
    }
  }

  /**
   * Get EMI status based on due date and payment status
   */
  getEMIStatus(repayment: Repayment, currentDate: Date = new Date()): string {
    const emiDate = new Date(repayment.DueDate);
    emiDate.setHours(0, 0, 0, 0);
    
    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0);
    
    const state = repayment.FinalRepaymentState || repayment.State;
    
    if (state === 'Paid') {
      return 'Paid';
    } else if (emiDate < today) {
      return 'Overdue';
    } else if (emiDate.getTime() === today.getTime()) {
      return 'Due Today';
    } else if (this.isWithinDays(emiDate, today, 30)) {
      return 'Upcoming';
    } else {
      return 'To Be Paid';
    }
  }

  // ============================================================================
  // VOUCHER UTILITIES
  // ============================================================================

  /**
   * Format voucher code with hyphens (XXXX-XXXX-XXXX-XXXX)
   */
  formatVoucherCode(code: string): string {
    if (!code) return '';
    
    const cleanCode = code.replace(/[-\s]/g, '');
    const groups = cleanCode.match(/.{1,4}/g);
    
    return groups ? groups.join('-') : code;
  }

  // ============================================================================
  // VALIDATION UTILITIES
  // ============================================================================

  /**
   * Validate email format
   */
  validateEmail(email: string): { valid: boolean; error?: string } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) {
      return { valid: false, error: 'Email is required' };
    }
    
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }
    
    return { valid: true };
  }

  /**
   * Validate mobile number format (Indian)
   */
  validateMobile(mobile: string): { valid: boolean; error?: string } {
    const cleanNumber = mobile.replace(/\D/g, '');
    
    if (cleanNumber.length !== 10) {
      return { valid: false, error: 'Mobile number must be 10 digits' };
    }
    
    if (!/^[6-9]/.test(cleanNumber)) {
      return { valid: false, error: 'Please enter a valid mobile number' };
    }
    
    return { valid: true };
  }

  // ============================================================================
  // DEVICE FINGERPRINTING
  // ============================================================================

  /**
   * Get device information for tracking
   */
  getDeviceInfo(): DeviceInfo {
    if (typeof window === 'undefined') {
      return { deviceId: '', os: 'Unknown', browser: 'Unknown', device: 'Unknown' };
    }

    // Try to use ClientJS if available
    if (window.ClientJS) {
      try {
        const client = new window.ClientJS();
        return {
          deviceId: client.getFingerprint().toString(),
          os: client.getOS(),
          browser: client.getBrowser(),
          device: client.getDevice()
        };
      } catch {
        // Fall through to manual fingerprinting
      }
    }

    // Manual fingerprinting fallback
    const nav = navigator;
    const screen = window.screen;
    
    const fingerprint = [
      nav.userAgent,
      nav.language,
      screen.colorDepth,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset()
    ].join('|');
    
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return {
      deviceId: Math.abs(hash).toString(36),
      os: this.detectOS(),
      browser: this.detectBrowser(),
      device: this.detectDevice()
    };
  }

  private detectOS(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Win') !== -1) return 'Windows';
    if (userAgent.indexOf('Mac') !== -1) return 'MacOS';
    if (userAgent.indexOf('Linux') !== -1) return 'Linux';
    if (userAgent.indexOf('Android') !== -1) return 'Android';
    if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
    return 'Unknown';
  }

  private detectBrowser(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Chrome') !== -1) return 'Chrome';
    if (userAgent.indexOf('Firefox') !== -1) return 'Firefox';
    if (userAgent.indexOf('Safari') !== -1) return 'Safari';
    if (userAgent.indexOf('Edge') !== -1) return 'Edge';
    if (userAgent.indexOf('Opera') !== -1) return 'Opera';
    return 'Unknown';
  }

  private detectDevice(): string {
    const userAgent = navigator.userAgent;
    if (/Mobi|Android/i.test(userAgent)) return 'Mobile';
    if (/Tablet|iPad/i.test(userAgent)) return 'Tablet';
    return 'Desktop';
  }

  // ============================================================================
  // RAZORPAY PAYMENT
  // ============================================================================

  /**
   * Initiate Razorpay payment
   */
  async initiateRazorpayPayment(config: {
    amount: number;
    mambuId: string;
    applicationId: string;
    email: string;
    contact: string;
    paymentType: PaymentType;
    onSuccess: (response: RazorpayResponse) => void;
    onDismiss?: () => void;
    onExternalWallet?: (response: RazorpayExternalResponse) => void;
    disablePaytm?: boolean;
  }): Promise<void> {
    const {
      amount,
      mambuId,
      applicationId,
      email,
      contact,
      paymentType,
      onSuccess,
      onDismiss,
      onExternalWallet,
      disablePaytm = true
    } = config;

    const options: RazorpayOptions = {
      key: RAZORPAYID,
      amount: Math.round(amount * 100), // Convert to paise
      description: mambuId,
      image: '/assets/images/logos/zest-logo.png',
      handler: onSuccess,
      prefill: {
        email,
        contact
      },
      notes: {
        ApplicationId: applicationId,
        PaymentSource: 'MY_ACCOUNTS',
        PaymentMode: 'QUICK_PAY',
        Type: paymentType
      },
      theme: {
        color: '#2CBE43'
      },
      modal: {
        ondismiss: () => {
          onDismiss?.();
        }
      }
    };

    // Add Paytm external wallet if not disabled
    if (!disablePaytm && onExternalWallet) {
      options.external = {
        wallets: ['paytm'],
        handler: (response: RazorpayExternalResponse) => {
          response.loanApplicationId = applicationId;
          response.mambuId = mambuId;
          response.paymentType = paymentType;
          onExternalWallet(response);
        }
      };
    }

    if (typeof window !== 'undefined' && window.Razorpay) {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } else {
      throw new Error('Razorpay is not loaded');
    }
  }

  // ============================================================================
  // XSS PROTECTION
  // ============================================================================

  /**
   * Sanitize HTML content to prevent XSS attacks
   * Use this instead of dangerouslySetInnerHTML directly
   */
  sanitizeHTML(html: string): string {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'div', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: ['href', 'target', 'class', 'style']
    });
  }

  /**
   * Create sanitized HTML for React's dangerouslySetInnerHTML
   */
  createSanitizedMarkup(html: string): { __html: string } {
    return { __html: this.sanitizeHTML(html) };
  }

  // ============================================================================
  // PRECLOSE CALCULATION
  // ============================================================================

  /**
   * Check if loan is eligible for preclose
   */
  isEligibleForPreclose(loan: Loan): boolean {
    const activeLoanStatuses = ['Active', 'InArrears', 'Approved'];
    
    if (!activeLoanStatuses.includes(loan.Status)) {
      return false;
    }

    // Check if moratorium is active
    if (loan.MoratoriumStatus === 'OPTIN_COMPLETED') {
      return false;
    }

    // Check if loan has remaining repayments
    if (loan.NumberOfRemainingRepayments <= 0) {
      return false;
    }

    return true;
  }
}

// Export singleton instance
const utilsService = new UtilsService();
export default utilsService;
